import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import LibraryFilters, { FilterState } from "@/components/library/LibraryFilters";
import SharedNoteCard, { SharedNote } from "@/components/SharedNoteCard";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, Upload, BookOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Library = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [notes, setNotes] = useState<SharedNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    level: "school",
    category: "",
    semester: "",
    subject: "",
    query: "",
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchNotes = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("shared_notes" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) {
        toast({ title: "Failed to load library", variant: "destructive" });
      } else {
        setNotes((data as unknown as SharedNote[]) || []);
      }
      setLoading(false);
    };
    fetchNotes();
  }, [user, toast]);

  const visible = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    return notes.filter((n) => {
      if (n.education_level !== filters.level) return false;
      if (filters.category && n.category !== filters.category) return false;
      if (filters.semester && n.semester !== filters.semester) return false;
      if (filters.subject && n.subject !== filters.subject) return false;
      if (q) {
        const hay = `${n.title} ${n.chapter ?? ""} ${n.subject} ${n.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [notes, filters]);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Notes Library — Free Student Notes | NoteGenius"
        description="Browse and download free study notes shared by school and college students. Filter by class, stream, semester, and subject."
        path="/library"
      />
      <Navbar />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="w-8 h-8 text-primary" />
              Notes Library
            </h1>
            <p className="text-muted-foreground mt-1">
              Free notes shared by fellow students. Pick your class or stream and start studying.
            </p>
          </div>
          <Button variant="hero" onClick={() => navigate("/share-notes")}>
            <Upload className="w-4 h-4 mr-2" /> Share your notes
          </Button>
        </div>

        <LibraryFilters value={filters} onChange={setFilters} />

        <div className="mt-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-20 glass rounded-2xl">
              <p className="text-muted-foreground mb-4">
                No notes match your filters yet. Be the first to share!
              </p>
              <Button variant="hero" onClick={() => navigate("/share-notes")}>
                <Upload className="w-4 h-4 mr-2" /> Upload notes
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visible.map((n) => (
                <SharedNoteCard
                  key={n.id}
                  note={n}
                  onDeleted={(id) => setNotes((prev) => prev.filter((x) => x.id !== id))}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Library;
